import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ReactNode } from "react";

import APPrintablePage from "../../../../components/APPrintablePage";
import APCalculation from "../../../../components/calculations/APCalculation";
import APCalculationInput from "../../../../components/calculations/APCalculationInput";
import APCalculationOutput from "../../../../components/calculations/APCalculationOutput";
import APPeriodicCalculations from "../../../../components/calculations/APPeriodicCalculation";
import { toDollars } from "../../../../helpers/currencies";
import { FinanceComparisonCalculations as FinanceComparisonCalculationsType } from "../../../../types/Calculation";

type DisplayedFields = {
  onroadVehiclePrice?: boolean;
  homeLoanTermMths?: boolean;
  loanRepayment?: boolean;

  termMths?: boolean;
  totalPayments?: boolean;

  homeLoanInterestRate?: boolean;
  loanPayout?: boolean;

  residualIncGst?: boolean;
  runningCosts?: boolean;
  lifetimeCost?: boolean;
  interest?: boolean;
};

export const FINANCE_COMPARISON_HOME_LOAN_ALL_FIELDS: DisplayedFields = {
  onroadVehiclePrice: true,
  homeLoanTermMths: true,
  loanRepayment: true,

  termMths: true,
  totalPayments: true,

  homeLoanInterestRate: true,
  loanPayout: true,

  lifetimeCost: true,
  residualIncGst: true,
  runningCosts: true,
  interest: true,
};

const FinanceComparisonHomeLoanCalculations = ({
  calculations,
  displayedFields,
  pageLinks,
}: {
  calculations: FinanceComparisonCalculationsType;
  displayedFields: DisplayedFields;
  pageLinks?: ReactNode;
}) => {
  const { inputs, homeloan } = calculations;

  return (
    <APPrintablePage
      topContent={
        <Stack spacing={2}>
          {pageLinks}
          <Box>
            {displayedFields.onroadVehiclePrice && (
              <APCalculationInput
                name="Onroad Vehicle Price"
                value={inputs.onroadVehiclePrice}
                currency
              />
            )}
            {displayedFields.homeLoanTermMths && (
              <APCalculationInput
                name="Home Loan Term Months"
                value={inputs.homeLoanTermMths}
              />
            )}

            {displayedFields.loanRepayment && (
              <APCalculationOutput
                calculation={homeloan.loanRepayment}
                currency
              />
            )}
          </Box>
          <Box>
            {displayedFields.termMths && (
              <APCalculationInput name="Term Months" value={inputs.termMths} />
            )}
            {displayedFields.totalPayments && (
              <APCalculationOutput
                calculation={homeloan.totalPayments}
                currency
              />
            )}
          </Box>
          <Box>
            {displayedFields.homeLoanInterestRate && (
              <APCalculationInput
                name="Home Loan Interest Rate"
                value={inputs.homeLoanInterestRate}
                percentage
              />
            )}

            {displayedFields.loanPayout && (
              <APCalculationOutput calculation={homeloan.loanPayout} currency />
            )}
          </Box>
          <Box>
            {displayedFields.residualIncGst && (
              <APCalculationInput
                name="Residual Inc GST"
                value={inputs.residual.incGst}
                currency
              />
            )}
            {displayedFields.runningCosts && (
              <APCalculationInput
                name="Running Costs"
                value={inputs.runningCosts.total}
                currency
              />
            )}
            {displayedFields.lifetimeCost && (
              <APCalculation
                name={homeloan.lifetimeCost.name}
                value={toDollars(homeloan.lifetimeCost.result.total, 2)}
                tooltip={homeloan.lifetimeCost.formula}
              />
            )}
            {displayedFields.interest && (
              <APCalculationOutput calculation={homeloan.interest} currency />
            )}
          </Box>
        </Stack>
      }
      bottomContent={
        <Box sx={{ maxWidth: 750 }}>
          {displayedFields.lifetimeCost && (
            <APPeriodicCalculations
              calculations={[
                {
                  name: "Lifetime Cost",
                  amounts: homeloan.lifetimeCost.result,
                },
              ]}
            />
          )}
        </Box>
      }
    />
  );
};

export default FinanceComparisonHomeLoanCalculations;
